/**
 * WB4 Depth Layer — WorkbenchTimelineEvent SSOT (Phase A).
 * Common language for Timeline · Audit · ActionFeedback · Undo · Explain.
 */

import type { WorkbenchKernelExecutionSurface } from './action-types';
import { resolveWorkbenchGrammarProxy } from './workbench-domain-grammar-proxy';

export type WorkbenchTimelineEventType =
  | 'ai_suggested'
  | 'user_approved'
  | 'action_executed'
  | 'field_updated'
  | 'status_changed'
  | 'flow_started'
  | 'flow_step_completed'
  | 'task_created'
  | 'document_generated'
  | 'evidence_uploaded'
  | 'validation_failed'
  | 'comment_added'
  | 'undo_executed'
  | 'revision_restored'
  | 'correction_requested'
  | 'correction_approved'
  | 'correction_rejected'
  | 'reversal_created'
  | 'domain_entity_created'
  | 'domain_entity_updated'
  | 'domain_entity_deleted';

export type WorkbenchRiskTier = 'low' | 'medium' | 'high' | 'finance';

export type WorkbenchTimelineSeverity = 'info' | 'success' | 'warning' | 'danger';

import type { WorkbenchTimelineRecoverabilityMode } from './workbench-recoverability-ladder';
export type { WorkbenchTimelineRecoverabilityMode } from './workbench-recoverability-ladder';
import { resolveTimelineRecoverabilityMode } from './workbench-recoverability-ladder';

export type WorkbenchTimelineEvent = {
  id: string;
  tenantId: string;
  domain: string;
  objectType: string;
  objectId: string;
  type: WorkbenchTimelineEventType;
  title: string;
  description?: string;
  actor: {
    userId: string;
    displayName?: string;
    role?: string;
  };
  action?: {
    actionId: string;
    actionLabel: string;
    riskTier: WorkbenchRiskTier;
    surface: WorkbenchKernelExecutionSurface;
  };
  change?: {
    fieldKey?: string;
    before?: unknown;
    after?: unknown;
    diffSummary?: string;
  };
  related?: {
    flowId?: string;
    taskId?: string;
    documentId?: string;
    evidenceId?: string;
    auditEventId?: string;
    revisionId?: string;
    undoSnapshotId?: string;
    domainEventType?: string;
  };
  recoverability?: {
    mode: WorkbenchTimelineRecoverabilityMode;
    available: boolean;
    expiresAt?: string;
    reason?: string;
  };
  severity?: WorkbenchTimelineSeverity;
  createdAt: string;
};

export type WorkbenchTimelineEventInput = Omit<WorkbenchTimelineEvent, 'id' | 'createdAt'> & {
  id?: string;
  createdAt?: string;
};

export function buildWorkbenchTimelineEventId(prefix = 'wtl'): string {
  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

export function normalizeWorkbenchTimelineEvent(
  input: WorkbenchTimelineEventInput
): WorkbenchTimelineEvent {
  return {
    ...input,
    id: input.id ?? buildWorkbenchTimelineEventId(),
    createdAt: input.createdAt ?? new Date().toISOString(),
    severity: input.severity ?? 'info',
  };
}

export interface WorkbenchTimelineObjectRef {
  domain: string;
  objectType: string;
  objectId: string;
}

export function buildWorkbenchTimelineObjectRefKey(objectRef: WorkbenchTimelineObjectRef): string {
  return `${objectRef.domain}/${objectRef.objectType}/${objectRef.objectId}`;
}

export interface WorkbenchAuxiliarySummary {
  objectRef: WorkbenchTimelineObjectRef;
  counts: {
    timeline: number;
    audit: number;
    evidence: number;
    validationIssues: number;
  };
  lastEvent?: WorkbenchTimelineEvent;
  recoverability?: {
    undoAvailable: boolean;
    revisionAvailable: boolean;
    correctionAvailable: boolean;
  };
}

const BUSINESS_DOMAIN_OBJECT_TYPE: Record<string, { domain: string; objectType: string }> = {
  sellable_item: { domain: 'item', objectType: 'sellable' },
  raw_material_item: { domain: 'item', objectType: 'raw-material' },
  external_entity: { domain: 'entity', objectType: 'external' },
  internal_entity: { domain: 'entity', objectType: 'internal' },
  finance_state: { domain: 'finance', objectType: 'state' },
  finance_flow: { domain: 'finance', objectType: 'flow' },
  finance_budget: { domain: 'finance', objectType: 'budget' },
  warehouse_picking_list: { domain: 'warehouse', objectType: 'picking-list' },
  warehouse_receiving: { domain: 'warehouse', objectType: 'receiving' },
  inventory_shortage: { domain: 'inventory', objectType: 'shortage' },
  manufacturing_bom: { domain: 'manufacturing', objectType: 'bom' },
  production_order: { domain: 'manufacturing', objectType: 'production-order' },
  quality_inspection: { domain: 'manufacturing', objectType: 'quality-inspection' },
  workspace_operation: { domain: 'workspace', objectType: 'operations' },
  stream_execution: { domain: 'flow', objectType: 'executions' },
  knowledge_report: { domain: 'knowledge', objectType: 'report' },
  knowledge_policy: { domain: 'knowledge', objectType: 'policy' },
  knowledge_plan: { domain: 'knowledge', objectType: 'plan' },
  assignment_execution: { domain: 'flow', objectType: 'assignments' },
  task_execution: { domain: 'flow', objectType: 'assignments' },
};

export function resolveWorkbenchTimelineObjectRef(input: {
  objectId: string;
  domain?: string;
  subdomain?: string;
  businessDomain?: string;
}): WorkbenchTimelineObjectRef {
  const objectId = input.objectId.trim();
  const normalizedBusinessDomain = input.businessDomain?.trim();
  const grammarProxy = normalizedBusinessDomain
    ? resolveWorkbenchGrammarProxy(normalizedBusinessDomain)
    : null;
  const fromBusinessDomain = normalizedBusinessDomain
    ? BUSINESS_DOMAIN_OBJECT_TYPE[normalizedBusinessDomain]
    : undefined;
  const domain = (
    input.domain?.trim() ||
    grammarProxy?.domainTop ||
    fromBusinessDomain?.domain ||
    'workspace'
  ).toLowerCase();
  const objectType = (
    input.subdomain?.trim() ||
    grammarProxy?.subdomain ||
    fromBusinessDomain?.objectType ||
    normalizedBusinessDomain ||
    'entity'
  ).toLowerCase();
  return { domain, objectType, objectId };
}

export function buildTimelineEventFromKernelAction(input: {
  tenantId: string;
  timelineRef: WorkbenchTimelineObjectRef;
  action: { id: string; label: string; handlerRef: string };
  actor: { userId: string; displayName?: string; role?: string };
  recoverabilityTier?: WorkbenchRiskTier;
  revisionSnapshotId?: string;
  undoable?: boolean;
  surface?: NonNullable<WorkbenchTimelineEvent['action']>['surface'];
  /** Client outbox-first — pre-generated timeline row id. */
  id?: string;
  change?: WorkbenchTimelineEvent['change'];
  severity?: WorkbenchTimelineSeverity;
  eventType?: Extract<
    WorkbenchTimelineEventType,
    | 'action_executed'
    | 'field_updated'
    | 'status_changed'
    | 'reversal_created'
    | 'validation_failed'
  >;
}): WorkbenchTimelineEventInput {
  const riskTier = input.recoverabilityTier ?? 'low';
  const undoable = input.undoable ?? false;
  const recoverabilityAvailable = Boolean(input.revisionSnapshotId) || undoable;
  const defaultType =
    riskTier === 'finance' ? 'reversal_created' : (input.eventType ?? 'action_executed');
  return {
    ...(input.id ? { id: input.id } : {}),
    tenantId: input.tenantId,
    domain: input.timelineRef.domain,
    objectType: input.timelineRef.objectType,
    objectId: input.timelineRef.objectId,
    type: defaultType,
    title: input.action.label,
    actor: input.actor,
    action: {
      actionId: input.action.id,
      actionLabel: input.action.label,
      riskTier,
      surface: input.surface ?? 'button',
    },
    ...(input.change ? { change: input.change } : {}),
    ...(input.revisionSnapshotId ? { related: { revisionId: input.revisionSnapshotId } } : {}),
    recoverability: {
      mode: resolveTimelineRecoverabilityMode(riskTier, recoverabilityAvailable),
      available: recoverabilityAvailable,
    },
    severity: input.severity ?? 'success',
  };
}

/** Low-tier inline field edit — before/after change contract for timeline. */
export function buildInlineFieldTimelineChange(input: {
  fieldKey: string;
  fieldLabel: string;
  previousValue: string;
  nextValue: string;
}): NonNullable<WorkbenchTimelineEvent['change']> {
  return {
    fieldKey: input.fieldKey,
    before: input.previousValue,
    after: input.nextValue,
    diffSummary: `${input.fieldLabel}: "${input.previousValue}" → "${input.nextValue}"`,
  };
}

/** WB4-DND1 — task → assignee drag-drop timeline change. */
export function buildDragDropAssignTaskTimelineChange(input: {
  taskLabel: string;
  assigneeLabel: string;
  previousAssigneeLabel?: string;
}): NonNullable<WorkbenchTimelineEvent['change']> {
  const before = input.previousAssigneeLabel?.trim() || '미배정';
  return {
    fieldKey: 'assignee',
    before,
    after: input.assigneeLabel,
    diffSummary: input.previousAssigneeLabel
      ? `${input.taskLabel}: ${before} → ${input.assigneeLabel}`
      : `${input.taskLabel} → ${input.assigneeLabel}`,
  };
}

/** WB4-DND1 — document → evidence panel drag-drop timeline change. */
export function buildDragDropAttachEvidenceTimelineChange(input: {
  documentLabel: string;
  evidencePanelLabel: string;
}): NonNullable<WorkbenchTimelineEvent['change']> {
  return {
    fieldKey: 'evidence_attachment',
    before: '미첨부',
    after: input.evidencePanelLabel,
    diffSummary: `${input.documentLabel} → ${input.evidencePanelLabel}`,
  };
}

/** WB4-DND1 — order → production queue drag-drop timeline change. */
export function buildDragDropStartProductionFlowTimelineChange(input: {
  orderLabel: string;
  queueLabel: string;
}): NonNullable<WorkbenchTimelineEvent['change']> {
  return {
    fieldKey: 'production_queue',
    before: '대기',
    after: input.queueLabel,
    diffSummary: `${input.orderLabel} → ${input.queueLabel}`,
  };
}

/** WB4 OD3-C — order → picking queue drag-drop timeline change. */
export function buildDragDropRouteToPickingQueueTimelineChange(input: {
  orderLabel: string;
  pickingQueueLabel: string;
}): NonNullable<WorkbenchTimelineEvent['change']> {
  return {
    fieldKey: 'picking_queue',
    before: '대기',
    after: input.pickingQueueLabel,
    diffSummary: `${input.orderLabel} → ${input.pickingQueueLabel}`,
  };
}

/** WB4 OD3-C — customer → collection queue drag-drop timeline change. */
export function buildDragDropRouteToCollectionQueueTimelineChange(input: {
  customerLabel: string;
  collectionQueueLabel: string;
}): NonNullable<WorkbenchTimelineEvent['change']> {
  return {
    fieldKey: 'collection_queue',
    before: '미배정',
    after: input.collectionQueueLabel,
    diffSummary: `${input.customerLabel} → ${input.collectionQueueLabel}`,
  };
}

/** WB4 OD3-C — document → approval packet drag-drop timeline change. */
export function buildDragDropAttachApprovalPacketTimelineChange(input: {
  documentLabel: string;
  approvalPacketLabel: string;
}): NonNullable<WorkbenchTimelineEvent['change']> {
  return {
    fieldKey: 'approval_packet',
    before: '미첨부',
    after: input.approvalPacketLabel,
    diffSummary: `${input.documentLabel} → ${input.approvalPacketLabel}`,
  };
}

/** WB4 OD3-C — inventory shortage → purchase request drag-drop timeline change. */
export function buildDragDropCreatePurchaseRequestTimelineChange(input: {
  shortageLabel: string;
  purchaseRequestLabel: string;
}): NonNullable<WorkbenchTimelineEvent['change']> {
  return {
    fieldKey: 'purchase_request',
    before: '없음',
    after: input.purchaseRequestLabel,
    diffSummary: `${input.shortageLabel} → ${input.purchaseRequestLabel}`,
  };
}

/** WB4 DND-OBJ — document → order object link drag-drop timeline change. */
export function buildDragDropLinkDocumentToOrderTimelineChange(input: {
  documentLabel: string;
  orderLabel: string;
}): NonNullable<WorkbenchTimelineEvent['change']> {
  return {
    fieldKey: 'linked_order',
    before: '미연결',
    after: input.orderLabel,
    diffSummary: `${input.documentLabel} → ${input.orderLabel}`,
  };
}

/** WB4 DND-OBJ — order → customer object link drag-drop timeline change. */
export function buildDragDropLinkOrderToCustomerTimelineChange(input: {
  orderLabel: string;
  customerLabel: string;
}): NonNullable<WorkbenchTimelineEvent['change']> {
  return {
    fieldKey: 'linked_customer',
    before: '미연결',
    after: input.customerLabel,
    diffSummary: `${input.orderLabel} → ${input.customerLabel}`,
  };
}

export function timelineEventToAuxiliaryRecord(event: WorkbenchTimelineEvent): {
  id: string;
  label: string;
  detail: string;
} {
  const actorLabel = event.actor.displayName?.trim() || event.actor.userId;
  const changeHint = event.change?.diffSummary?.trim();
  if (
    event.type === 'correction_requested' ||
    event.type === 'correction_approved' ||
    event.type === 'correction_rejected'
  ) {
    const reason = event.description?.trim() || event.recoverability?.reason?.trim() || '승인 보정';
    return {
      id: `timeline-correction-${event.id}`,
      label: event.title,
      detail: `${event.createdAt} · ${actorLabel} · ${reason}`,
    };
  }
  return {
    id: `timeline-event-${event.id}`,
    label: event.title,
    detail: changeHint
      ? `${event.createdAt} · ${actorLabel} · ${changeHint}`
      : `${event.createdAt} · ${actorLabel}`,
  };
}
